import React from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  ChartBar, Users, Wallet, ListChecks, TrendUp, ClockCounterClockwise,
  IdentificationCard, ChartLine, Headset, SignOut, Receipt, BookOpen, Gear, Eye, FileText,
  Bank
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out');
  };

  const navSections = [
    { section: 'Overview', items: [{ path: '/admin', icon: ChartBar, label: 'Dashboard' }] },
    {
      section: 'Client Management',
      items: [
        { path: '/admin/clients', icon: Users, label: 'Clients' },
        { path: '/admin/accounts', icon: Wallet, label: 'Trading Accounts' },
        { path: '/admin/kyc', icon: IdentificationCard, label: 'KYC Review' },
        { path: '/admin/activity', icon: ClockCounterClockwise, label: 'Login Activity' },
      ]
    },
    {
      section: 'Trading',
      items: [
        { path: '/admin/orders', icon: ListChecks, label: 'Orders' },
        { path: '/admin/positions', icon: TrendUp, label: 'Positions' },
        { path: '/admin/instruments', icon: ChartLine, label: 'Instruments' },
      ]
    },
    {
      section: 'Finance',
      items: [
        { path: '/admin/transactions', icon: Receipt, label: 'Transactions' },
        { path: '/admin/payment-settings', icon: Bank, label: 'Payment Settings' },
      ]
    },
    {
      section: 'Content',
      items: [
        { path: '/admin/knowledge', icon: BookOpen, label: 'Knowledge Hub' },
        { path: '/admin/legal-documents', icon: FileText, label: 'Legal Documents' },
      ]
    },
    {
      section: 'WebTrader',
      items: [
        { path: '/admin/webtrader', icon: ChartLine, label: 'Open WebTrader' },
        { path: '/admin/webtrader-settings', icon: Gear, label: 'WebTrader Settings' },
        { path: '/admin/webtrader-preview', icon: Eye, label: 'WebTrader Preview' },
      ]
    },
    {
      section: 'Operations',
      items: [
        { path: '/admin/tickets', icon: Headset, label: 'Support' },
        { path: '/admin/audit', icon: ClockCounterClockwise, label: 'Audit Logs' },
        { path: '/admin/footer-settings', icon: Gear, label: 'Footer Settings' },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="w-64 bg-slate-900 text-white min-h-screen flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <Link to="/" data-testid="admin-brand-link" className="block hover:opacity-80 transition-opacity">
            <div className="text-xl font-heading font-black">WellingtonCDFS</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">Admin Panel</div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
          {navSections.map((section) => (
            <div key={section.section}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-3">
                {section.section}
              </div>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                      location.pathname === item.path
                        ? 'bg-brand-accent text-white'
                        : 'text-slate-300 hover:bg-slate-800'
                    }`}
                  >
                    <item.icon size={18} weight="bold" />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="text-sm mb-2">
            <div className="font-semibold">{user?.full_name}</div>
            <div className="text-xs text-slate-400">{user?.role}</div>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-white w-full"
          >
            <SignOut size={18} /> Sign Out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
